import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, type InvitePublic, type SessionResponse } from '@bingo/shared';
import { AuthCard } from '../../components/AuthCard.js';
import { ApiError, api } from '../../lib/api.js';
import { adoptSession } from '../../lib/session.js';

/**
 * /convite/:token — a porta de entrada de quem foi convidado.
 *
 * Duas telas em uma, decididas pelo servidor (`hasAccount`): quem já usa o
 * produto só confirma; quem não usa escolhe nome e senha. Perguntar "você já
 * tem conta?" seria jogar para o usuário uma informação que o servidor já
 * tem — e que ele frequentemente erraria.
 *
 * Aceitar **já deixa a pessoa logada**: o convite é prova de identidade
 * suficiente para esta sessão, e mandá-la para o login logo depois de criar
 * a senha seria pedir a mesma informação duas vezes seguidas.
 */

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'dono',
  ADMIN: 'administrador',
  OPERATOR: 'operador',
};

export function Convite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InvitePublic | null | 'invalid'>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;

    void api<InvitePublic>(`/api/invites/${token}`, { anonymous: true })
      .then((data) => {
        setInvite(data);
        setName(data.name ?? '');
      })
      .catch(() => setInvite('invalid'));
  }, [token]);

  if (done) return <Navigate to="/inicio" replace />;

  if (invite === null) {
    return (
      <AuthCard title="Convite">
        <p className="hint-text">Carregando…</p>
      </AuthCard>
    );
  }

  if (invite === 'invalid') {
    return (
      <AuthCard
        title="Convite indisponível"
        subtitle="Este link não vale mais — pode ter vencido, já ter sido usado ou ter sido cancelado."
      >
        {/* Mesma mensagem para todos os motivos: dizer QUAL deles entregaria
            a quem está adivinhando que o token existe. */}
        <p className="hint-text">Peça um link novo a quem administra a organização.</p>
      </AuthCard>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      const session = await api<SessionResponse>(`/api/invites/${token}/accept`, {
        method: 'POST',
        anonymous: true,
        body: invite.hasAccount ? {} : { name: name.trim(), password },
      });

      adoptSession(session);
      setDone(true);
      navigate('/inicio', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível aceitar o convite.');
      setBusy(false);
    }
  };

  return (
    <AuthCard
      title={`Convite para ${invite.organizationName}`}
      subtitle={`Você entra como ${ROLE_LABEL[invite.role] ?? invite.role}.`}
      footer={<span className="auth-note">Convite para {invite.email}</span>}
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
        {invite.hasAccount ? (
          <p className="hint-text">
            Já existe uma conta com este e-mail. Toque em entrar para participar da organização —
            sua senha continua a mesma.
          </p>
        ) : (
          <>
            <label className="field">
              <span>Seu nome</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                required
              />
            </label>

            <label className="field">
              <span>Crie uma senha</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                required
              />
            </label>
            <p className="hint-text">Pelo menos {PASSWORD_MIN_LENGTH} caracteres.</p>
          </>
        )}

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </AuthCard>
  );
}
